import { useState } from 'react'
import { Button, Typography, Modal, Stack, CircularProgress } from '@mui/material'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import {
  ReaderTaskSubmissionFormValues,
  readerTaskSubmissionSchema,
} from '../../schemas/ReaderTaskSubmissionValidationSchema'
import DecisionStep from './DecisionStep'
import DisagreeStep from './DisagreeStep'
import EsignStep from './EsignStep'
import { useParams } from 'react-router-dom'
import { ReadTaskSummaryData } from '@common/config/api/client'
import { useAuth } from 'react-oidc-context'
import { storeSigningIntent } from '@auth/utils/storeSigningIntent'
import { useReadTaskESignMutation } from '../../hooks/useReadTaskESignMutation'
import { useRestoreSigningProcess } from '@auth/hooks/useRestoreSigningProcess'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: 2,
  bgcolor: 'background.default',
  boxShadow: 8,
  padding: 3,
  width: 600,
}

type ModalStep = 'decision' | 'disagree' | 'esign'

interface AdjudicationDecisionModalProps {
  taskId: string
  timepoint: ReadTaskSummaryData['timepoints'][0]
  initialModalStep?: string
  disabled?: boolean
  readerSetEndTime?: (end_time: Date | number) => void
  pauseReaderActiveTime?: () => void
}

export default function AdjudicationDecisionModal({
  timepoint,
  taskId,
  initialModalStep,
  disabled = false,
  readerSetEndTime,
  pauseReaderActiveTime,
}: AdjudicationDecisionModalProps) {
  const [open, setOpen] = useState(initialModalStep === 'esign')
  const [currentStep, setCurrentStep] = useState<ModalStep>(initialModalStep === 'esign' ? 'esign' : 'decision')
  const [isSigningIn, setIsSigningIn] = useState(false)
  const { user } = useExtendedAuth()
  const { studyId } = useParams<{ studyId: string }>()
  const { signinRedirect } = useAuth()

  const readTaskSignMutation = useReadTaskESignMutation()

  const { isSigningInProgress } = useRestoreSigningProcess(readTaskSignMutation, {
    successMessage: 'Read task signed successfully',
    errorMessage: 'Failed to complete read task signing',
  })

  const methods = useForm<ReaderTaskSubmissionFormValues>({
    resolver: zodResolver(readerTaskSubmissionSchema),
    defaultValues: {
      username: user?.profile.preferred_username,
      context: {
        type: 'reader_task_submission',
        read_task_submission: {
          adjudication_agreed: false,
          adjudication_selected_read_task_id: '',
          adjudication_justification_id: '',
          justification_comment: '',
          comment: '',
        },
      },
    },
  })

  const { watch } = methods
  const readTaskSubmission = watch('context.read_task_submission')

  const handleClose = () => {
    setOpen(false)
    setCurrentStep('decision')
    methods.reset()
  }

  const handleNext = async () => {
    if (currentStep === 'decision' || currentStep === 'disagree') {
      const isValid = await methods.trigger([
        'context.read_task_submission.adjudication_selected_read_task_id',
        'context.read_task_submission.comment',
        'context.read_task_submission.adjudication_justification_id',
        'context.read_task_submission.justification_comment',
      ])

      if (!isValid) return

      setCurrentStep('esign')
    } else {
      if (readerSetEndTime && pauseReaderActiveTime) {
        readerSetEndTime(Date.now())
        pauseReaderActiveTime()
      }
      await requestReAuthentication()
    }
  }

  const handleBack = () => {
    if (currentStep === 'esign') {
      setCurrentStep(readTaskSubmission.adjudication_agreed ? 'decision' : 'disagree')
    }
  }

  const toggleDisagree = () => {
    methods.setValue('context.read_task_submission.adjudication_agreed', false)
    methods.setValue('context.read_task_submission.adjudication_selected_read_task_id', undefined)
    methods.setValue('context.read_task_submission.justification_comment', undefined)
    setCurrentStep('disagree')
  }

  const getSignerName = () => {
    const firstName = user?.profile.given_name || ''
    const lastName = user?.profile.family_name || ''
    let signerName = `${firstName} ${lastName}`.trim()

    if (!signerName) {
      signerName = user?.profile.email || user?.profile.preferred_username || ''
    }

    return signerName
  }

  const requestReAuthentication = async () => {
    setIsSigningIn(true)

    const readTaskSubmission = methods.getValues('context.read_task_submission')

    const payload = {
      type: 'reader_task_submission',
      read_task_submission: {
        adjudication_agreed: readTaskSubmission.adjudication_agreed,
        adjudication_justification_id: readTaskSubmission.adjudication_justification_id,
        justification_comment: readTaskSubmission.justification_comment ?? '',
        comment: readTaskSubmission.comment,
        ...(readTaskSubmission.adjudication_selected_read_task_id
          ? { adjudication_selected_read_task_id: readTaskSubmission.adjudication_selected_read_task_id }
          : {}),
      },
    }

    sessionStorage.setItem(
      'reader_modal_state',
      JSON.stringify({
        taskId,
        step: 'esign',
      }),
    )

    storeSigningIntent(payload, {
      queryParams: {
        read_task_id: taskId,
        study_id: studyId!,
      },
      signer_name: getSignerName(),
    })

    await signinRedirect({
      prompt: 'login',
      max_age: 0,
      redirect_uri: window.location.href,
    })
  }

  const renderModalContent = () => {
    switch (currentStep) {
      case 'decision':
        return <DecisionStep toggleDisagree={toggleDisagree} timepoint={timepoint} />
      case 'disagree':
        return <DisagreeStep />
      case 'esign':
        return <EsignStep />
    }
  }

  const renderActions = () => {
    const isLoading = isSigningInProgress || isSigningIn

    return (
      <Stack justifyContent="space-between" direction="row" alignSelf="stretch" alignItems="center">
        <Button
          variant="contained"
          sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText' }}
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Stack direction="row" spacing={2}>
          {currentStep === 'esign' && (
            <Button
              variant="contained"
              sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </Button>
          )}
          <Button
            variant="contained"
            sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}
            onClick={handleNext}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoading ? 'Signing...' : currentStep === 'esign' ? 'Sign' : 'Next'}
          </Button>
        </Stack>
      </Stack>
    )
  }

  return (
    <>
      <Stack
        onClick={(e) => {
          if (!disabled) {
            setOpen(true)
            e.stopPropagation()
          }
        }}
        data-testid="reopen-task-button"
      >
        <Button
          size="large"
          variant="contained"
          disabled={disabled}
          sx={{
            backgroundColor: 'primary.main',
          }}
        >
          <Typography
            variant="button"
            sx={{
              color: 'primary.contrastText',
            }}
          >
            Decide
          </Typography>
        </Button>
      </Stack>
      <Modal open={open} onClick={(e) => e.stopPropagation()} onClose={handleClose} data-testid="adjudication-modal">
        <FormProvider {...methods}>
          <Stack sx={modalStyle} spacing={2} component="form">
            {renderModalContent()}
            {renderActions()}
          </Stack>
        </FormProvider>
      </Modal>
    </>
  )
}
